// Doubtful-debt provisions & receivable write-offs —
// doubtful_debt_provisions.* / receivable_writeoffs.* (Finance)
//
// Two registers, four keys, one module: estimate what will not be collected,
// then give up on the parts that were not.
//
// The backend keys were approved by the central key service on 21 Sep 2026 and
// are registered by the amer-laravel migration
// `database/migrations/2026_09_21_000002_add_finance_close_and_control_and_department_restore_permissions.php`
// — it is no longer parked in `database/migrations/pending-central-approval/`,
// so the routes name keys that exist and the feature is reachable. The ids below
// are that service's **approval numbers**, which the migration writes as each
// permission row's own `permissions.id` — the two have to stay equal. An id here
// that does not match its backend row fails silently: `HAS_PERMISSION` simply
// returns false, so the page, the sidebar leaf and every gated button never
// appear, with nothing logged anywhere. Read an issued id; never derive the next
// one from the last.

// ─── Doubtful-debt provisions ────────────────────────────────────────────────

/** doubtful_debt_provisions.index — the run register, and opening a run in it. */
export const DOUBTFUL_DEBT_PROVISIONS_INDEX = 1623;

/**
 * There is no `doubtful_debt_provisions.show` key — the August 2026 audit
 * collapsed detail routes into their module's `index`, and the route file guards
 * `GET /{id}` with `index` accordingly.
 *
 * Aliased rather than pointed at a key that does not exist: a constant naming a
 * missing key makes `HAS_PERMISSION` false forever and the record page vanishes
 * for everyone.
 */
export const DOUBTFUL_DEBT_PROVISIONS_SHOW = DOUBTFUL_DEBT_PROVISIONS_INDEX;

/**
 * doubtful_debt_provisions.post — recording a run and posting its movement.
 *
 * **It also guards `/preview`**, which is a GET that writes nothing. That is
 * deliberate on the backend and must be mirrored here: a preview is guarded by
 * the action it serves, because there is no `<module>.show`-style key for a form
 * helper. So the worksheet route is gated on this key rather than on `index` —
 * a user who could open the form but not preview it would meet a 400 on the one
 * button the page exists for.
 */
export const DOUBTFUL_DEBT_PROVISIONS_POST = 1624;

// ─── Receivable write-offs ───────────────────────────────────────────────────

/** receivable_writeoffs.index — the register, and opening a write-off in it. */
export const RECEIVABLE_WRITEOFFS_INDEX = 1625;

/** See the note on `DOUBTFUL_DEBT_PROVISIONS_SHOW` — same audit, same reason. */
export const RECEIVABLE_WRITEOFFS_SHOW = RECEIVABLE_WRITEOFFS_INDEX;

/**
 * receivable_writeoffs.create — writing a receivable off, and with it raising the
 * Art. 40 credit note where relief is claimed.
 *
 * There is deliberately **no update, no delete and no restore key**, because the
 * backend exposes no such routes: a posted write-off moved the ledger and may
 * have cleared a credit note with ZATCA, so it is corrected by reversing its
 * journal rather than by editing or removing the record. Do not add constants
 * for keys that do not exist — a gated button that can never be true reads as one
 * somebody forgot to finish.
 */
export const RECEIVABLE_WRITEOFFS_CREATE = 1626;
